<div class="statistics-container">
  <!-- Permission check: Show message if user doesn't have permission -->
  <div *ngIf="!canViewStatistics" class="no-permission-message">
    <mat-card>
      <mat-card-content>
        <mat-icon color="warn" style="font-size: 48px; width: 48px; height: 48px;">lock</mat-icon>
        <h2>Access Restricted</h2>
        <p>You do not have permission to view statistics. Please contact your practice administrator to request access.</p>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Full statistics view (only shown if user has permission) -->
  <ng-container *ngIf="canViewStatistics">

  <div class="statistics-header">
    <div class="header-content">
      <h1>
        Practice Stats

        <div class="header-actions" *ngIf="statsType">
          <button mat-icon-button (click)="loadStatistics()" [disabled]="loading" class="refresh-button">
            <mat-icon>refresh</mat-icon>
          </button>
          <button mat-raised-button color="primary" (click)="openPdfExportDialog()"
            [disabled]="loading || !statistics || pdfExportLoading" class="export-button">
            <mat-icon>{{ pdfExportLoading ? 'hourglass_empty' : 'picture_as_pdf' }}</mat-icon>
            {{ pdfExportLoading ? 'Generating PDF...' : 'Export as PDF' }}
          </button>
          <!-- The EthiQal Recognition report. One click, no filters: the period is not a
               preference, it is whichever half-year the practice is being recognised for, and
               offering a choice is how the wrong one gets submitted. The tooltip names the
               period so it is knowable without generating it. -->
          <button mat-raised-button (click)="runEthiqalExport()" *ngIf="showEthiqal"
            [disabled]="loading || ethiqalExportLoading"
            [matTooltip]="'Exports ' + ethiqalPeriodLabel + ' — the last completed half-year, excluding consent data'"
            class="export-button recognition-button">
            <mat-icon>{{ ethiqalExportLoading ? 'hourglass_empty' : 'verified' }}</mat-icon>
            {{ ethiqalExportLoading ? 'Generating EthiQal Recognition...' : 'EthiQal Recognition' }}
          </button>

          <!-- The general-purpose Recognition Export: any range, consent included. HIDDEN rather
               than removed — see `showRecognitionExport`. Everything below it (the menu, the
               presets, the server's range handling) still works and is one flag from returning. -->
          <button mat-raised-button [matMenuTriggerFor]="recognitionMenu"
            *ngIf="showRecognitionExport"
            [disabled]="loading || twelveMonthPdfExportLoading"
            class="export-button recognition-button">
            <mat-icon>{{ twelveMonthPdfExportLoading ? 'hourglass_empty' : 'date_range' }}</mat-icon>
            {{ twelveMonthPdfExportLoading ? 'Generating Recognition Export...' : 'Recognition Export' }}
          </button>
          <!-- Kept outside the `*ngIf`: the panel is inert without a trigger, and putting it
               inside would make the template reference above resolve to nothing. -->
          <mat-menu #recognitionMenu="matMenu" class="date-range-menu-panel">
            <!-- Seeded with the last 12 whole months, so the report this button used to produce
                 is still one press of Apply away. -->
            <app-date-range-menu
              [startDate]="recognitionStartDate"
              [endDate]="recognitionEndDate"
              [maxDate]="maxDate"
              [presets]="recognitionPresets"
              applyLabel="Export"
              hint="Reported as whole calendar months — a part month would read as a drop in activity rather than a month still in progress."
              (rangeApplied)="recognitionMenu.closed.emit(); onRecognitionRangeApplied($event)">
            </app-date-range-menu>
          </mat-menu>
        </div>
      </h1>
      <!--  <div class="date-range-display" *ngIf="statsType && selectedDateRange">
        <span class="date-range-text">{{ dateRangeDisplayText }}</span>
      </div> -->

      <!-- Filter Section -->
      <div class="filter-section">
        <div class="filter-wrapper">
          <div class="filter-controls">

            <div class="filter-group" *ngIf="showFinancialStats">
              <label class="filter-label">Stats Type:</label>
              <mat-chip-listbox [(ngModel)]="statsType">
                <mat-chip-option value="clinical">Clinical Stats </mat-chip-option>
                <mat-chip-option value="financial">Financial Stats </mat-chip-option>
              </mat-chip-listbox>
            </div>

            <!-- Date Range Filter -->
            <div class="filter-group" *ngIf="statsType">
              <label class="filter-label">Date Range:</label>
              <mat-chip-listbox [(ngModel)]="selectedDateRange" (change)="onDateRangeChange($event)"
                [disabled]="loading">
                <!-- The Custom chip carries the same panel. Selecting it no longer reloads the
                     page: choosing a range means touching two fields, and the state in between
                     is always a range nobody asked for. Apply is what loads. -->
                <mat-chip-option *ngFor="let chip of dateRangeChips" [value]="chip.value"
                  [matMenuTriggerFor]="chip.value === 'custom' ? customRangeMenu : null">
                  {{ chip.label }}
                </mat-chip-option>
              </mat-chip-listbox>
            </div>

            <mat-menu #customRangeMenu="matMenu" class="date-range-menu-panel">
              <app-date-range-menu
                [startDate]="pendingCustomStartDate"
                [endDate]="pendingCustomEndDate"
                [maxDate]="maxDate"
                (rangeApplied)="customRangeMenu.closed.emit(); onCustomRangeApplied($event)">
              </app-date-range-menu>
            </mat-menu>
          </div>
        </div>
      </div>
    </div>
  </div>



  <!-- Loading State with Skeleton Loaders -->
  <div *ngIf="statsType && loading" class="statistics-content">
    <!-- Clinical Stats Skeleton -->

    <ng-container *ngIf="statsType === 'clinical'">
      <div class="stats-section">
        <h2>Clinical Stats</h2>
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Charts Row Skeleton -->
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-chart">
                <div class="skeleton-bar" *ngFor="let item of [1,2,3,4,5]"></div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Top Diagnoses and ICD Codes Skeleton -->
        <div class="detailed-stats">
          <mat-card class="detailed-card skeleton-card">
            <mat-card-header>
              <div class="skeleton-card-title"></div>
            </mat-card-header>
            <mat-card-content>
              <div class="period-stats">
                <div class="stat-item skeleton-item" *ngFor="let item of [1,2,3,4,5]">
                  <div class="skeleton-label"></div>
                  <div class="skeleton-number"></div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="detailed-card skeleton-card">
            <mat-card-header>
              <div class="skeleton-card-title"></div>
            </mat-card-header>
            <mat-card-content>
              <div class="period-stats">
                <div class="stat-item skeleton-item" *ngFor="let item of [1,2,3,4,5]">
                  <div class="skeleton-label"></div>
                  <div class="skeleton-number"></div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Referral Sources Skeleton -->
      <div class="stats-section">
        <h2>Referral Sources</h2>
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-chart">
                <div class="skeleton-pie" *ngFor="let item of [1,2,3,4,5]"></div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Appointment Type Overview Skeleton -->
      <div class="stats-section">
        <h2>Appointment Type Overview</h2>
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2,3]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Appointment Status Statistics Skeleton -->
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2,3,4]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Appointment Types List Skeleton -->
        <div class="detailed-stats">
          <mat-card class="detailed-card skeleton-card">
            <mat-card-header>
              <div class="skeleton-card-title"></div>
            </mat-card-header>
            <mat-card-content>
              <div class="period-stats">
                <div class="stat-item skeleton-item" *ngFor="let item of [1,2,3,4,5]">
                  <div class="skeleton-label"></div>
                  <div class="skeleton-number"></div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Inpatient Statistics Skeleton -->
      <div class="stats-section">
        <h2>Inpatient Statistics</h2>
        <div class="hospital-filter-container">
          <div class="skeleton-hospital-filter"></div>
        </div>

        <!-- Row 1: Total Inpatients and Total Discharges -->
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Row 2: Total Readmissions and Avg Length of Stay -->
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Row 3: Charts -->
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-chart">
                <div class="skeleton-bar" *ngFor="let item of [1,2,3,4,5]"></div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Procedure Statistics Skeleton -->
      <div class="stats-section">
        <h2>Procedure Statistics</h2>
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Consenting Overview Skeleton -->
      <div class="stats-section">
        <h2>Consenting Overview</h2>

        <!-- Onboarding Consent Subsection Skeleton -->
        <h3>Onboarding Consent</h3>
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2,3,4]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Procedural Consent Subsection Skeleton -->
        <h3>Procedural Consent</h3>
        <div class="stats-grid">
          <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2,3,4]">
            <mat-card-content>
              <div class="stat-header">
                <div class="skeleton-icon"></div>
                <div class="skeleton-title"></div>
              </div>
              <div class="skeleton-value"></div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>
    </ng-container>

    <ng-container *ngIf="statsType === 'financial'">
      <div class="statistics-content">
        <!-- Financial Statistics Skeleton -->
        <div class="stats-section">
          <h2>Financial Statistics</h2>
          <div class="stats-grid">
            <mat-card class="stat-card skeleton-card" *ngFor="let item of [1,2,3]">
              <mat-card-content>
                <div class="stat-header">
                  <div class="skeleton-icon"></div>
                  <div class="skeleton-title"></div>
                </div>
                <div class="skeleton-value"></div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>

        <!-- Financial Calendar Skeleton -->
        <div class="stats-section">
          <h2>Financial Calendar</h2>
          <div class="calendar-section">
            <div class="skeleton-calendar">
              <div class="skeleton-calendar-header"></div>
              <div class="skeleton-calendar-grid">
                <div class="skeleton-day"
                  *ngFor="let item of [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35]">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </div>

  <div *ngIf="statsType && error" class="error-container">
    <mat-card class="error-card">
      <mat-card-content>
        <mat-icon>error_outline</mat-icon>
        <p>Failed to load statistics. Please try again.</p>
        <button mat-raised-button color="primary" (click)="loadStatistics()">Retry</button>
      </mat-card-content>
    </mat-card>
  </div>

  <ng-container *ngIf="statsType === 'clinical'">
    <div *ngIf="!loading && !error && statistics" class="statistics-content">
      <!-- Clinical Stats -->
      <div class="stats-section">
        <h2>Clinical Stats</h2>
        <div class="stats-grid">
          <!-- First row: Total Patients Seen and Mean Age -->
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>calendar_month</mat-icon>
                <h3>Total Patients Seen</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('patientsSeen')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.clinical.patientsSeen }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>elderly</mat-icon>
                <h3>Mean Age</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button (click)="openHistoryChart('meanAge')"
                  class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.clinical.meanAge }} years</div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Second row: Charts -->
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>bar_chart</mat-icon>
                <h3>Age Distribution</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('ageDistribution')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="age-chart-container">
                <div style="height: 200px; width: 100%;">
                  <ngx-charts-bar-vertical class="ngx-chart" [results]="ageDistribution" [xAxis]="true" [yAxis]="true"
                    [showXAxisLabel]="true" [showYAxisLabel]="true" [xAxisLabel]="'Age Group'" [yAxisLabel]="'Patients'"
                    [scheme]="'vivid'" [roundEdges]="true" [animations]="true" [showDataLabel]="true">
                  </ngx-charts-bar-vertical>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>pie_chart</mat-icon>
                <h3>Gender Breakdown</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('genderBreakdown')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div #genderPie class="gender-pie-container">
                <ngx-charts-pie-chart class="ngx-chart" [results]="genderPieData" [legend]="true"
                  [legendPosition]="legendPosition" [labels]="false" [scheme]="genderColorScheme" [animations]="true">
                </ngx-charts-pie-chart>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Top Diagnoses and ICD Codes -->
        <div class="detailed-stats">
          <mat-card class="detailed-card">
            <mat-card-header>
              <mat-card-title>Top 5 Diagnoses</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="period-stats">
                <div class="stat-item" *ngFor="let diagnosis of statistics.clinical.topDiagnoses">
                  <span class="stat-label">{{ diagnosis.diagnosis }}</span>
                  <span class="stat-number">{{ diagnosis.count }}</span>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="detailed-card">
            <mat-card-header>
              <mat-card-title>Top 5 ICD-10 Codes</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="period-stats">
                <div class="stat-item" *ngFor="let icd of statistics.clinical.topICDCodes">
                  <span class="stat-label">{{ icd.code }} - {{ icd.description }}</span>
                  <span class="stat-number">{{ icd.count }}</span>
                </div>
              </div>
            </mat-card-content>
          </mat-card>


        </div>
      </div>


      <!-- Morbidity/Mortality Statistics -->
      <div class="stats-section">
        <h2>Morbidity & Mortality Statistics</h2>
        <div class="stats-grid">
          <!-- Morbidity Cases -->
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>warning</mat-icon>
                <h3>Morbidity Cases</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('morbidityCases')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.clinical.morbidityMortality.morbidity.total }}</div>
              <div class="morbidity-pie-container" *ngIf="statistics.clinical.morbidityMortality.morbidity.total > 0">
                <ngx-charts-pie-chart class="ngx-chart" [results]="morbidityPieData" [legend]="true"
                  [legendPosition]="legendPosition" [labels]="false" [scheme]="morbidityColorScheme"
                  [animations]="true">
                </ngx-charts-pie-chart>
              </div>
              <div class="no-data-message" *ngIf="statistics.clinical.morbidityMortality.morbidity.total === 0">
                <mat-icon>info</mat-icon>
                <p>No morbidity cases recorded for this period</p>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Mortality Cases -->
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>favorite</mat-icon>
                <h3>Mortality Cases</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('mortalityCases')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.clinical.morbidityMortality.mortality.total }}</div>
              <div class="mortality-pie-container" *ngIf="statistics.clinical.morbidityMortality.mortality.total > 0">
                <ngx-charts-pie-chart class="ngx-chart" [results]="mortalityPieData" [legend]="true"
                  [legendPosition]="legendPosition" [labels]="false" [scheme]="mortalityColorScheme"
                  [animations]="true">
                </ngx-charts-pie-chart>
              </div>
              <div class="no-data-message" *ngIf="statistics.clinical.morbidityMortality.mortality.total === 0">
                <mat-icon>info</mat-icon>
                <p>No mortality cases recorded for this period</p>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Referral Sources -->
      <div class="stats-section">
        <h2>Referral Sources</h2>
        <div class="stats-grid">
          <!-- Referral Source Statistics -->
        <mat-card class="stat-card">
          <mat-card-content>
            <div class="stat-header">
              <mat-icon>source</mat-icon>
              <h3>Referral Sources</h3>
              <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                (click)="openHistoryChart('referralSources')" class="history-chart-btn">
                <mat-icon>auto_graph</mat-icon>
              </button>
            </div>
            <div #referralPie class="referral-pie-container">
              <ngx-charts-pie-chart class="ngx-chart" [results]="referralPieData" [legend]="true"
                [legendPosition]="legendPosition" [labels]="false" [scheme]="referralColorScheme" [animations]="true">
              </ngx-charts-pie-chart>
            </div>
          </mat-card-content>
        </mat-card>
        </div>
      </div>

      <!-- Appointment Type Overview -->
      <div class="stats-section">
        <h2>Appointment Type Overview</h2>
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>event</mat-icon>
                <h3>Total Scheduled Events</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalScheduledEvents')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ getTotalScheduledEvents() }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>schedule</mat-icon>
                <h3>Avg Waiting Time</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('avgWaitingTime')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
                <mat-icon
                  matTooltip="Average time patients wait from their scheduled appointment time until they arrive. Only includes non-procedure appointments with complete arrival and start time data."
                  matTooltipPosition="above" class="info-icon">info</mat-icon>
              </div>
              <div class="stat-value">{{ formattedOverallWaitingTime }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>timer</mat-icon>
                <h3>Total Time Spent</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalTimeSpent')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
                <mat-icon
                  matTooltip="Total time spent on scheduled events (excluding procedures). Calculated from the difference between start and end times of appointments. Note: For accurate data, entire consults should be recorded."
                  matTooltipPosition="above" class="info-icon">info</mat-icon>
              </div>
              <div class="stat-value">{{ formattedTotalTimeSpent }}</div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Appointment Status Statistics -->
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>cancel</mat-icon>
                <h3>Total Cancelled</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalCancelled')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.appointmentTypeOverview.totalCancelled }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>person_off</mat-icon>
                <h3>Total Did Not Attend</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalDidNotAttend')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.appointmentTypeOverview.totalDidNotAttend }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>trending_down</mat-icon>
                <h3>Cancellation Rate</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('cancellationRate')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ getCancellationRate() }}%</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>trending_down</mat-icon>
                <h3>No-Show Rate</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button (click)="openHistoryChart('noShowRate')"
                  class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ getNoShowRate() }}%</div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Appointment Types List -->
        <div class="detailed-stats">
          <mat-card class="detailed-card">
            <mat-card-header>
              <mat-card-title>Appointment Types</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="period-stats">
                <div class="stat-item"
                  *ngFor="let appointmentType of statistics.appointmentTypeOverview.appointmentTypes">
                  <div class="appointment-type-info">
                    <div class="appointment-type-color" [style.background-color]="appointmentType.backgroundColor"
                      [style.color]="appointmentType.textColor">
                      {{ appointmentType.type }}
                    </div>
                    <span class="appointment-type-label" *ngIf="appointmentType.isProcedure">(Procedure)</span>
                  </div>
                  <div class="appointment-type-stats">
                    <span class="stat-number">{{ appointmentType.totalScheduled }}</span>
                    <span class="waiting-time" *ngIf="appointmentType.avgDuration !== null"
                      matTooltip="Average duration for this appointment type. Only calculated for non-procedure appointments with complete start and end time data. Note: For accurate data, entire consults should be recorded."
                      matTooltipPosition="left">
                      {{ formatMinutes(appointmentType.avgDuration) }}
                    </span>
                    <span class="waiting-time no-data" *ngIf="appointmentType.avgDuration === null"
                      matTooltip="No duration data available. This may be a procedure appointment or there may be no appointments with complete start and end time data in the selected date range. Note: For accurate data, entire consults should be recorded."
                      matTooltipPosition="left">
                      No data
                    </span>
                  </div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Inpatient Statistics -->
      <div class="stats-section">
        <h2>Inpatient Statistics</h2>
        <div class="hospital-filter-container" *ngIf="hospitals.length > 0">
          <mat-form-field appearance="outline" class="hospital-filter">
            <mat-select [(ngModel)]="selectedHospitalId" (selectionChange)="onHospitalChange($event)"
              placeholder="All Hospitals">
              <mat-option value="">All Hospitals</mat-option>
              <mat-option *ngFor="let hospital of hospitals" [value]="hospital.id">
                {{ hospital.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <!-- Row 1: Total Inpatients and Total Discharges -->
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>hotel</mat-icon>
                <h3>Total Inpatients</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalInpatients')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.inpatient.totalInpatients }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>exit_to_app</mat-icon>
                <h3>Total Discharges</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalDischarges')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.inpatient.totalDischarges }}</div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Row 2: Total Readmissions and Avg Length of Stay -->
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>repeat</mat-icon>
                <h3>Total Readmissions</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalReadmissions')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.inpatient.totalReadmissions }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>schedule</mat-icon>
                <h3>Avg Length of Stay</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('avgLengthOfStay')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.inpatient.avgLengthOfStay }} days</div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Row 3: Length of Stay Breakdown and Admission Types Charts -->
        <div class="stats-grid">
          <mat-card class="stat-card" *ngIf="lengthOfStayChartData && lengthOfStayChartData.length > 0">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>bar_chart</mat-icon>
                <h3>Length of Stay Breakdown</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('lengthOfStayBreakdown')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="length-of-stay-chart-container">
                <ngx-charts-bar-vertical class="ngx-chart" [results]="lengthOfStayChartData" [xAxis]="true"
                  [yAxis]="true" [showXAxisLabel]="true" [showYAxisLabel]="true" [xAxisLabel]="'Length of Stay'"
                  [yAxisLabel]="'Patients'" [scheme]="'vivid'" [roundEdges]="true" [animations]="true"
                  [showDataLabel]="true">
                </ngx-charts-bar-vertical>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card" *ngIf="admissionTypesPieData && admissionTypesPieData.length > 0">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>pie_chart</mat-icon>
                <h3>Admission Types</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('admissionTypes')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="admission-types-chart-container">
                <ngx-charts-pie-chart class="ngx-chart" [results]="admissionTypesPieData" [legend]="true"
                  [legendPosition]="legendPosition" [labels]="false" [scheme]="'vivid'" [animations]="true">
                </ngx-charts-pie-chart>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

      </div>

      <!-- Procedure Statistics -->
      <div class="stats-section">
        <h2>Procedure Statistics</h2>
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>medical_services</mat-icon>
                <h3>Total Procedures</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalProcedures')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.totalProcedures }}</div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Consenting Overview -->
      <div class="stats-section">
        <h2>Consenting Overview</h2>

        <!-- Onboarding Consent Subsection -->
        <h3>Onboarding Consent</h3>
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>assignment_turned_in</mat-icon>
                <h3>Consents Completed</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('onboardingConsentsCompleted')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.onboardingConsents.completed }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>pending</mat-icon>
                <h3>Consents Pending</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('onboardingConsentsPending')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.onboardingConsents.pending }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>mail_outline</mat-icon>
                <h3>Consents Not Sent</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('onboardingConsentsNotSent')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.onboardingConsents.notSent }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>verified</mat-icon>
                <h3>Compliance Percentage</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('onboardingConsentsCompliance')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.onboardingConsents.compliancePercentage }}%</div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Procedural Consent Subsection -->
        <h3>Procedural Consent</h3>
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>assignment_turned_in</mat-icon>
                <h3>Consents Completed</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('procedureConsentsCompleted')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.procedureConsents.completed }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>pending</mat-icon>
                <h3>Consents Pending</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('procedureConsentsPending')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.procedureConsents.pending }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>mail_outline</mat-icon>
                <h3>Consents Not Sent</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('procedureConsentsNotSent')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.procedureConsents.notSent }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>verified</mat-icon>
                <h3>Compliance Percentage</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('procedureConsentsCompliance')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ statistics.procedures.procedureConsents.compliancePercentage }}%</div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Financial Statistics -->
      <!--  -->
    </div>
  </ng-container>
  <ng-container *ngIf="statsType === 'financial'">
    <div *ngIf="!loading && !error && statistics" class="statistics-content">
      <div class="stats-section">
        <h2>Financial Stats</h2>
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>receipt</mat-icon>
                <h3>Total Invoices</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalInvoices')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ formattedInvoiceAmount }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>payments</mat-icon>
                <h3>Total Receipts</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalReceipts')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ formattedReceiptAmount }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>account_balance_wallet</mat-icon>
                <h3>Total Outstanding</h3>
                <button *ngIf="selectedDateRange !== 'custom'" mat-icon-button
                  (click)="openHistoryChart('totalOutstanding')" class="history-chart-btn">
                  <mat-icon>auto_graph</mat-icon>
                </button>
              </div>
              <div class="stat-value">{{ formattedOutstandingAmount }}</div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Connect (medical-aid claims) Statistics -->
      <div *ngIf="connectEnabled && connectFinancials" class="stats-section">
        <h2>Connect Claims</h2>
        <div class="stats-grid">
          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>request_quote</mat-icon>
                <h3>Claimed</h3>
              </div>
              <div class="stat-value">{{ formattedConnectClaimedAmount }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>price_check</mat-icon>
                <h3>Paid</h3>
              </div>
              <div class="stat-value">{{ formattedConnectPaidAmount }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>hourglass_bottom</mat-icon>
                <h3>Outstanding</h3>
              </div>
              <div class="stat-value">{{ formattedConnectOutstandingAmount }}</div>
            </mat-card-content>
          </mat-card>

          <mat-card class="stat-card">
            <mat-card-content>
              <div class="stat-header">
                <mat-icon>block</mat-icon>
                <h3>Rejected</h3>
              </div>
              <div class="stat-value">{{ formattedConnectRejectedAmount }}</div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- Financial Calendar -->
      <div class="stats-section">
        <h2>Financial Calendar</h2>
        <div class="calendar-section">
          <app-financial-calendar></app-financial-calendar>
        </div>
      </div>

    </div>
  </ng-container>
  </ng-container>
</div>

<!-- ── The export progress overlay ──────────────────────────────────────────────────
     ⚠ A TEMPLATE, ATTACHED TO THE CDK OVERLAY CONTAINER — NOT A DIV IN THIS COMPONENT.

     It used to be an inline `.loading-overlay` with `position: fixed` and `z-index: 9999999`,
     and it still rendered BEHIND the left navigation. The z-index was never the problem:
     Angular Material sets `.mat-drawer-content { position: relative; z-index: 1 }`, which opens
     a STACKING CONTEXT around everything routed into it. Inside that context the largest
     z-index in the world cannot climb past `.mat-drawer-side { z-index: 3 }` — the whole
     context is painted below the drawer as a unit, so the nav stayed lit and clickable over a
     modal progress screen.

     The CDK overlay container is appended to `<body>` at `z-index: 1000`, outside that context
     entirely, which is the only place a genuinely full-page overlay can live. -->
<ng-template #exportOverlay>
  <div class="loading-overlay">
    <div class="progress-container">
      <mat-progress-bar mode="determinate" [value]="exportProgress" class="progress-bar">
      </mat-progress-bar>
      <div class="progress-text">{{ exportProgress | number:'1.1-1' }}%</div>
    </div>
    <div class="loading-text">{{ currentLoadingMessage }}</div>
    <div class="loading-subtext" *ngIf="currentSubMessage">{{ currentSubMessage }}</div>
  </div>
</ng-template>
